import NumberFlow from '@number-flow/react'
import { useAtomValue } from 'jotai'
import type { ElementType, PropsWithChildren } from 'react'
import { useMemo } from 'react'
import { twJoin, twMerge } from 'tailwind-merge'
import GameIconsCrystalBars from '~icons/game-icons/crystal-bars'
import { localeAtom } from 'src/stores/intl'
import { useIsMobile } from 'src/utils/hooks/useBreakpoint'

export default function CreditMeter<T extends ElementType>({
  as = 'span',
  truncateOnMobile = false,
  value,
  originalValue,
  updating = false,
  children,
  iconClassName,
  valueClassName,
  ...props
}: PropsWithAs<T> &
  PropsWithChildren<{
    value?: number
    originalValue?: number
    updating?: boolean
    iconClassName?: string
    valueClassName?: string
    truncateOnMobile?: boolean
  }>) {
  const Root = as
  const isMobile = useIsMobile()
  const locale = useAtomValue(localeAtom)
  const shouldTruncate = truncateOnMobile && isMobile

  const format = useMemo(() => {
    if (!shouldTruncate) return undefined
    return {
      notation: 'compact' as const,
      compactDisplay: 'short' as const,
      maximumFractionDigits: 1,
    }
  }, [shouldTruncate])

  return (
    <Root {...props}>
      <span className="flex items-center gap-[0.25em]">
        <GameIconsCrystalBars className={twMerge('size-4', iconClassName)} />
        {children ?? (
          <span
            className={twJoin(
              'transition',
              updating && 'opacity-50',
              valueClassName,
            )}
          >
            {value != null ? (
              <NumberFlow
                value={value}
                format={format}
                locales={locale}
                className="flex items-center"
              />
            ) : (
              '—'
            )}
            {originalValue != null && (
              <span className="relative ml-[0.25em] inline-flex items-center text-[0.85em] opacity-80 after:pointer-events-none after:absolute after:inset-x-0 after:top-1/2 after:h-px after:-translate-y-1/2 after:bg-current">
                <NumberFlow
                  value={originalValue}
                  format={format}
                  locales={locale}
                  className="flex items-center"
                />
              </span>
            )}
          </span>
        )}
      </span>
    </Root>
  )
}
